"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.plannedSlotStatusToAbsenceType = exports.plannedSlotStatusCountsAsWork = exports.WORKED_ABSENCE_PLANNED_SLOT_STATUSES = exports.ABSENCE_COUNTS_AS_WORK = exports.ABSENCE_PLANNED_SLOT_STATUSES = exports.absenceTypeToPlannedSlotStatus = exports.assistantDayStatusSchema = exports.AssistantDayStatusEnum = exports.absenceOriginSchema = exports.AbsenceOriginEnum = exports.absenceStatusSchema = exports.AbsenceStatusEnum = exports.absenceTypeSchema = exports.AbsenceTypeEnum = void 0;
const zod_1 = require("zod");
exports.AbsenceTypeEnum = {
    paid_leave: 'paid_leave',
    sick: 'sick',
    exceptional: 'exceptional',
    public_holiday: 'public_holiday',
    maternity_leave: 'maternity_leave',
    birth_leave: 'birth_leave',
    unpaid_leave: 'unpaid_leave',
};
exports.absenceTypeSchema = zod_1.z.enum([
    'paid_leave',
    'sick',
    'exceptional',
    'public_holiday',
    'maternity_leave',
    'birth_leave',
    'unpaid_leave',
]);
exports.AbsenceStatusEnum = {
    pending: 'pending',
    approved: 'approved',
    rejected: 'rejected',
};
exports.absenceStatusSchema = zod_1.z.enum(['pending', 'approved', 'rejected']);
/**
 * Who created the absence record: the assistant through the self-service
 * request flow, or an admin (direct entry or planning-grid day override).
 */
exports.AbsenceOriginEnum = {
    assistant: 'assistant',
    admin: 'admin',
};
exports.absenceOriginSchema = zod_1.z.enum(['assistant', 'admin']);
exports.AssistantDayStatusEnum = {
    present: 'present',
    paid_leave: 'paid_leave',
    sick: 'sick',
    exceptional: 'exceptional',
    public_holiday: 'public_holiday',
    maternity_leave: 'maternity_leave',
    birth_leave: 'birth_leave',
    unpaid_leave: 'unpaid_leave',
};
exports.assistantDayStatusSchema = zod_1.z.enum([
    'present',
    'paid_leave',
    'sick',
    'exceptional',
    'public_holiday',
    'maternity_leave',
    'birth_leave',
    'unpaid_leave',
]);
/**
 * Marking a planning slot as an absence sets its status to the matching
 * `ON_*` value. These two helpers bridge the absence type (paid_leave/sick/
 * exceptional/public_holiday/maternity_leave/unpaid_leave) and the slot
 * status so the grid can render an absent slot like a request-based absence
 * cell, and the override use-case can map the other way.
 */
exports.absenceTypeToPlannedSlotStatus = {
    paid_leave: 'ON_PAID_LEAVE',
    sick: 'ON_SICK',
    exceptional: 'ON_EXCEPTIONAL',
    public_holiday: 'ON_PUBLIC_HOLIDAY',
    maternity_leave: 'ON_MATERNITY_LEAVE',
    birth_leave: 'ON_BIRTH_LEAVE',
    unpaid_leave: 'ON_UNPAID_LEAVE',
};
/**
 * Every planned-slot status that represents an absence (the `ON_*` values).
 * Derived from the absence-type bridge so it can never drift from it.
 */
exports.ABSENCE_PLANNED_SLOT_STATUSES = Object.values(exports.absenceTypeToPlannedSlotStatus);
/**
 * Whether each absence type counts toward the assistant's worked-hours
 * totals (planning weekly hours, modulation). Every absence counts — unpaid
 * leave (congé sans solde) included: it is not paid time, but it fills the
 * assistant's contract hours. Only the modulation's OVERTIME base narrows
 * further (real worked time + paid leave, see PreviewModulationsUseCase).
 */
exports.ABSENCE_COUNTS_AS_WORK = {
    paid_leave: true,
    public_holiday: true,
    maternity_leave: true,
    birth_leave: true,
    sick: true,
    unpaid_leave: true,
    exceptional: true,
};
/**
 * The `ON_*` statuses for absences that count as worked time. Built from
 * {@link ABSENCE_COUNTS_AS_WORK} so the two can never drift apart.
 *
 * @returns the planned-slot statuses whose minutes feed worked-hours totals
 */
function buildWorkedAbsenceStatuses() {
    const statuses = [];
    for (const type of exports.absenceTypeSchema.options) {
        if (exports.ABSENCE_COUNTS_AS_WORK[type]) {
            statuses.push(exports.absenceTypeToPlannedSlotStatus[type]);
        }
    }
    return statuses;
}
/** Planned-slot statuses (`ON_*`) for absences that count as worked time. */
exports.WORKED_ABSENCE_PLANNED_SLOT_STATUSES = buildWorkedAbsenceStatuses();
const WORKED_PLANNED_SLOT_STATUSES = new Set([
    'PLANNED',
    ...exports.WORKED_ABSENCE_PLANNED_SLOT_STATUSES,
]);
/**
 * Whether a planned-slot status contributes to worked-hours totals: a worked
 * `PLANNED` slot, or any absence status (unpaid leave included). Only
 * cancelled slots do not count.
 *
 * @param status - the planned-slot status to classify
 * @returns `true` when the slot's minutes should be summed into worked hours
 */
const plannedSlotStatusCountsAsWork = (status) => WORKED_PLANNED_SLOT_STATUSES.has(status);
exports.plannedSlotStatusCountsAsWork = plannedSlotStatusCountsAsWork;
const plannedSlotStatusToAbsenceTypeMap = {
    ON_PAID_LEAVE: 'paid_leave',
    ON_SICK: 'sick',
    ON_EXCEPTIONAL: 'exceptional',
    ON_PUBLIC_HOLIDAY: 'public_holiday',
    ON_MATERNITY_LEAVE: 'maternity_leave',
    ON_BIRTH_LEAVE: 'birth_leave',
    ON_UNPAID_LEAVE: 'unpaid_leave',
};
const plannedSlotStatusToAbsenceType = (status) => plannedSlotStatusToAbsenceTypeMap[status] ?? null;
exports.plannedSlotStatusToAbsenceType = plannedSlotStatusToAbsenceType;
